import { Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './hooks/useAuth';
import { Layout } from './components/Layout';
import { LandingPage } from './pages/LandingPage';
import { useDocumentMeta } from './hooks/useDocumentMeta';
import { SignInPage } from './pages/SignInPage';
import { SignUpPage } from './pages/SignUpPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { PricingPage } from './pages/PricingPage';
import { ProfilePage } from './pages/ProfilePage';
import { ApiKeysPage } from './pages/ApiKeysPage';
import { WebhooksPage } from './pages/WebhooksPage';
import { TeamPage } from './pages/TeamPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { SupportPage } from './pages/SupportPage';
import { SupportTicketPage } from './pages/SupportTicketPage';
import { AwardView } from './pages/public/AwardView';
import { AwardShare } from './pages/public/AwardShare';
import { AwardVerify } from './pages/public/AwardVerify';
import { RecipientPortal } from './pages/public/RecipientPortal';
import { TaskForcePool } from './pages/public/TaskForcePool';
import { RecipientProfile } from './pages/public/RecipientProfile';
import { NotFoundPage } from './pages/public/NotFoundPage';
import { IssuersPage } from './pages/IssuersPage';
import { BadgesPage } from './pages/BadgesPage';
import { MyTemplatesPage } from './pages/MyTemplatesPage';
import { AwardsPage } from './pages/AwardsPage';
import { BulkAwardsPage } from './pages/BulkAwardsPage';
import { BulkSendsPage, BulkSendJobPage } from './pages/BulkSendsPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { ActivityPage } from './pages/ActivityPage';
import { TestBadgeDesignerPage } from './pages/TestBadgeDesigner';
import { PublicDesignerPage } from './pages/PublicDesigner';
import { BadgeTemplatesGalleryPage } from './pages/BadgeTemplatesGallery';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
          <p className="text-sm text-gray-400">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  return <>{children}</>;
}

export function App() {
  useDocumentMeta();

  return (
    <Routes>
      {/* Landing is always visible — logged-in users can browse it too; the
          header CTA switches to "Go to Dashboard" (see LandingPage). */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/login" element={<Navigate to="/signin" replace />} />

      {/* Public badge-templates gallery hub + "Edit this template" designer */}
      <Route path="/badge-templates" element={<BadgeTemplatesGalleryPage />} />
      <Route path="/designer" element={<PublicDesignerPage />} />

      {/* Public award pages (no auth required) */}
      <Route path="/awards/:guid" element={<AwardView />} />
      <Route path="/awards/:guid/share" element={<AwardShare />} />
      <Route path="/verify/:guid" element={<AwardVerify />} />

      {/* Recipient portal (magic-link auth, NOT Cognito) */}
      <Route path="/me" element={<RecipientPortal />} />

      {/* Task-force self-claim pool (tokenized link, internal tool, NOT Cognito) */}
      <Route path="/tasks" element={<TaskForcePool />} />
      <Route path="/u/:handle" element={<RecipientProfile />} />

      {/* Team-invite acceptance — PUBLIC so a logged-out invitee can land here,
          sign up / sign in, and be returned to complete the join. */}
      <Route path="/accept-invite" element={<AcceptInvitePage />} />

      {/* Docs served by VitePress at /docs/ — not part of the React SPA */}

      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/issuers" element={<IssuersPage />} />
        <Route path="/badges" element={<BadgesPage />} />
        <Route path="/my-templates" element={<MyTemplatesPage />} />
        <Route path="/my-awards" element={<AwardsPage />} />
        <Route path="/bulk-awards" element={<BulkAwardsPage />} />
        <Route path="/bulk-sends" element={<BulkSendsPage />} />
        <Route path="/bulk-sends/:jobId" element={<BulkSendJobPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/activity" element={<ActivityPage />} />
        <Route path="/api-keys" element={<ApiKeysPage />} />
        <Route path="/webhooks" element={<WebhooksPage />} />
        <Route path="/members" element={<TeamPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/support/:ticketId" element={<SupportTicketPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/profile" element={<ProfilePage />} />
      </Route>

      {/* Dev-only standalone designer for E2E pixel-fit tests (not bundled in prod) */}
      {import.meta.env.DEV && (
        <Route path="/test/badge-designer" element={<TestBadgeDesignerPage />} />
      )}

      {/* Catch-all */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
